import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from './AuthContext';
import { Button, Input } from '@/components/ui';

interface RegisterFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const Register: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
  } = useForm<RegisterFormData>();

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setIsSubmitting(true);
    
    try {
      await registerUser(data.email, data.password, data.username);
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);

      if (error instanceof Error) {
        if (error.message.includes('email already exists')) {
          setError('email', { message: 'An account with this email already exists' });
        } else if (error.message.includes('username')) {
          setError('username', { message: 'This username is already taken' });
        } else if (error.message.includes('rate limit')) {
          console.error('Too many registration attempts. Please try again later.');
        } else {
          console.error(error.message);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-meradio-primary/10 to-meradio-secondary/10 flex items-center justify-center p-4">
      <div className="card max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold gradient-text">Meradio</h1>
          <p className="text-meradio-dark-light mt-2">Create your account</p>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-meradio-dark mb-1">
              Username
            </label>
            <Input
              id="username"
              type="text"
              placeholder="Choose a username"
              disabled={isSubmitting}
              error={errors.username?.message}
              {...register('username', {
                required: 'Username is required',
                minLength: {
                  value: 2,
                  message: 'Username must be at least 2 characters',
                },
                maxLength: {
                  value: 50,
                  message: 'Username cannot exceed 50 characters',
                },
                pattern: {
                  value: /^[a-zA-Z0-9_-]+$/,
                  message: 'Username can only contain letters, numbers, underscores, and hyphens',
                },
              })}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-meradio-dark mb-1">
              Email
            </label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              disabled={isSubmitting}
              error={errors.email?.message}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: 'Please enter a valid email address',
                },
              })}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-meradio-dark mb-1">
              Password
            </label>
            <Input
              id="password"
              type="password"
              placeholder="Create a password"
              disabled={isSubmitting}
              error={errors.password?.message}
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters',
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*$/,
                  message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
                },
              })}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-meradio-dark mb-1">
              Confirm Password
            </label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              disabled={isSubmitting}
              error={errors.confirmPassword?.message}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value: string) =>
                  value === password || 'Passwords do not match',
              })}
            />
          </div>

          <Button type="submit" fullWidth isLoading={isSubmitting} disabled={isSubmitting}>
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-sm text-meradio-dark-light">
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="text-meradio-primary hover:text-meradio-primary-dark font-medium transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>

        <div className="mt-4 p-3 bg-meradio-light/20 rounded-lg">
          <p className="text-xs text-meradio-dark-light text-center">
            By creating an account, you agree to Meradio's terms of service and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;